/* eslint-disable no-restricted-globals */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import {
  Match,
  MatchClient,
  BracketClient,
  TournamentClient,
  ScoreType,
  ScoreModel,
  EndMatchModel
} from '../../utils/apiClient';
import { useAuth } from '../Auth/AuthContext';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import './MatchConduct.scss';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';
import { createMatchHubConnection, MatchLiveState, MatClaimResult, MatLockDto } from '../../utils/matchHub';
import { getMatNumber, setMatNumber, getMatControllerId } from '../../utils/matSettings';

type MatchScoresSnapshot = {
  homeIppon: number;
  homeWazaAri: number;
  homeYuko: number;
  awayIppon: number;
  awayWazaAri: number;
  awayYuko: number;
  homeShido: number;
  awayShido: number;
};

const resolveSuggestedWinner = (s: MatchScoresSnapshot): boolean | null => {
  if (s.homeIppon > 0 && s.awayIppon === 0) return true;
  if (s.awayIppon > 0 && s.homeIppon === 0) return false;
  if (s.homeWazaAri >= 2 && s.awayWazaAri < 2) return true;
  if (s.awayWazaAri >= 2 && s.homeWazaAri < 2) return false;
  if (s.homeShido >= 3 && s.awayShido < 3) return false;
  if (s.awayShido >= 3 && s.homeShido < 3) return true;
  if (s.homeWazaAri !== s.awayWazaAri) return s.homeWazaAri > s.awayWazaAri;
  if (s.homeYuko !== s.awayYuko) return s.homeYuko > s.awayYuko;
  return null;
};

const apiErrorMessage = (ex: any): string | undefined => {
  const data = ex?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') {
    if (typeof data.detail === 'string') return data.detail;
    if (typeof data.title === 'string') return data.title;
  }
  return undefined;
};

export interface MatchConductProps {
  matchId: number;
  onStartedChange?: (isStarted: boolean) => void;
  onFinished?: (winnerName: string) => void;
}

const MatchConduct: React.FC<MatchConductProps> = ({ matchId, onStartedChange, onFinished }) => {
  const appContext = React.useContext(AppContext);
  const [match, setMatch] = useState<Match | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const { isAdmin, hasRole } = useAuth();

  const matchClient = new MatchClient(ApiUrl, axios);
  const bracketClient = new BracketClient(ApiUrl, axios);
  const tournamentClient = new TournamentClient(ApiUrl, axios);

  const [localStarted, setLocalStarted] = useState<boolean>(false);
  const [localPaused, setLocalPaused] = useState<boolean>(false);
  const [localStartAt, setLocalStartAt] = useState<number | null>(null);
  const [localPausedAccumMs, setLocalPausedAccumMs] = useState<number>(0);
  const [localLastPauseAt, setLocalLastPauseAt] = useState<number | null>(null);

  const [homeIppon, setHomeIppon] = useState<number>(0);
  const [homeWazaAri, setHomeWazaAri] = useState<number>(0);
  const [homeYuko, setHomeYuko] = useState<number>(0);
  const [awayIppon, setAwayIppon] = useState<number>(0);
  const [awayWazaAri, setAwayWazaAri] = useState<number>(0);
  const [awayYuko, setAwayYuko] = useState<number>(0);

  const [homeShido, setHomeShido] = useState<number>(0);
  const [awayShido, setAwayShido] = useState<number>(0);

  const [osaekomiActive, setOsaekomiActive] = useState<'home' | 'away' | null>(null);
  const [osaekomiStartTime, setOsaekomiStartTime] = useState<number | null>(null);
  const [osaekomiCurrentTime, setOsaekomiCurrentTime] = useState<number>(0);
  const [manualMode, setManualMode] = useState(false);
  const [manualMinutes, setManualMinutes] = useState(3);
  const [manualSeconds, setManualSeconds] = useState(0);
  const [savingManual, setSavingManual] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  // Refs нужны, чтобы из таймеров и async-колбэков читать актуальные значения
  // без перегенерации функций на каждый ререндер.
  const finishedHandledRef = useRef<boolean>(false);

  // Табло (страница /display/:matNumber): трансляция хода поединка на монитор зала.
  const [matNumber, setMatNumberState] = useState<string | null>(() => getMatNumber());
  const hubConnectionRef = useRef<signalR.HubConnection | null>(null);
  const matClaimedRef = useRef(false);
  const [matClaimed, setMatClaimed] = useState(false);
  const [matClaimError, setMatClaimError] = useState<string | null>(null);
  const [matLocks, setMatLocks] = useState<MatLockDto[]>([]);
  const controllerIdRef = useRef(getMatControllerId());

  // Снимок для хаба и истечения времени: timeRemaining специально читаем отсюда,
  // а не из замыкания эффекта (иначе на табло улетает остаток с момента прошлого клика).
  const liveSnapRef = useRef({
    match,
    matchId,
    matNumber,
    timeRemaining,
    timerRunning,
    localStarted,
    localPaused,
    homeIppon,
    homeWazaAri,
    homeYuko,
    homeShido,
    awayIppon,
    awayWazaAri,
    awayYuko,
    awayShido,
    osaekomiActive,
    osaekomiStartTime,
  });
  liveSnapRef.current = {
    match,
    matchId,
    matNumber,
    timeRemaining,
    timerRunning,
    localStarted,
    localPaused,
    homeIppon,
    homeWazaAri,
    homeYuko,
    homeShido,
    awayIppon,
    awayWazaAri,
    awayYuko,
    awayShido,
    osaekomiActive,
    osaekomiStartTime,
  };

  const getScoresSnapshot = (): MatchScoresSnapshot => {
    const s = liveSnapRef.current;
    return {
      homeIppon: s.homeIppon,
      homeWazaAri: s.homeWazaAri,
      homeYuko: s.homeYuko,
      awayIppon: s.awayIppon,
      awayWazaAri: s.awayWazaAri,
      awayYuko: s.awayYuko,
      homeShido: s.homeShido,
      awayShido: s.awayShido,
    };
  };

  useEffect(() => {
    onStartedChange?.(localStarted);
  }, [localStarted, onStartedChange]);

  useEffect(() => {
    const fetchMatch = async () => {
      try {
        setLoading(true);
        // Новый поединок (компонент переиспользуется без размонтирования) — сбрасываем
        // флаг «итог уже отправлен», иначе табло навсегда перестанет получать live-обновления.
        finishedHandledRef.current = false;
        const matchData = await matchClient.getMatch(matchId);
        const bracketData = await bracketClient.getBracket(matchData.tournamentBracketId);
        const tournamentData = await tournamentClient.getTournament(bracketData.tournamentId);
        const durationMs = (tournamentData.durationInMinutes || 0) * 60 * 1000;

        let remainingMs = durationMs;
        const regulationMin = tournamentData.durationInMinutes || 3;
        setManualMinutes(regulationMin);
        setManualSeconds(0);
        if (matchData.endTime && matchData.startTime) {
          const startTime = new Date(matchData.startTime).getTime();
          const endTime = new Date(matchData.endTime).getTime();
          const pausedMs = matchData.pausedTime?.totalMilliseconds || 0;
          const elapsedMs = Math.max(0, (endTime - startTime) - pausedMs);
          remainingMs = Math.max(0, durationMs - elapsedMs);
          setManualMinutes(Math.floor(elapsedMs / 60000));
          setManualSeconds(Math.floor((elapsedMs % 60000) / 1000));
          // Не дёргаем onFinished при загрузке уже завершённого поединка
          finishedHandledRef.current = true;
        }

        // Остаток и матч в одном батче — иначе табло успевает получить remaining=0.
        setHomeIppon(matchData.homeScoreIppon || 0);
        setHomeWazaAri(matchData.homeScoreWazaAri || 0);
        setHomeYuko(matchData.homeScoreYuko || 0);
        setAwayIppon(matchData.awayScoreIppon || 0);
        setAwayWazaAri(matchData.awayScoreWazaAri || 0);
        setAwayYuko(matchData.awayScoreYuko || 0);
        setHomeShido(matchData.homeShido || 0);
        setAwayShido(matchData.awayShido || 0);
        setTimeRemaining(remainingMs);
        setMatch(matchData);
      } catch (ex) {
        handleError(appContext!.setNotificationData, ex);
      } finally {
        setLoading(false);
      }
    };

    fetchMatch();
  }, [matchId, reloadToken]);

  const buildLiveState = useCallback((): MatchLiveState | null => {
    const s = liveSnapRef.current;
    if (!s.match || !s.matNumber) return null;

    const homeName = s.match.homeParticipant?.person
      ? `${s.match.homeParticipant.person.lastName} ${s.match.homeParticipant.person.firstName}`.trim()
      : 'Первый участник';
    const awayName = s.match.awayParticipant?.person
      ? `${s.match.awayParticipant.person.lastName} ${s.match.awayParticipant.person.firstName}`.trim()
      : 'Второй участник';

    return {
      matchId: s.match.id || s.matchId,
      homeName,
      awayName,
      homeIppon: s.homeIppon,
      homeWazaAri: s.homeWazaAri,
      homeYuko: s.homeYuko,
      homeShido: s.homeShido,
      awayIppon: s.awayIppon,
      awayWazaAri: s.awayWazaAri,
      awayYuko: s.awayYuko,
      awayShido: s.awayShido,
      isStarted: s.localStarted,
      isPaused: s.localPaused,
      timerAnchorUtc: new Date().toISOString(),
      timeRemainingMsAtAnchor: Number.isFinite(s.timeRemaining) ? Math.max(0, Math.round(s.timeRemaining)) : 0,
      isTimerRunning: s.timerRunning,
      osaekomiSide: s.osaekomiActive,
      osaekomiStartedUtc: s.osaekomiStartTime ? new Date(s.osaekomiStartTime).toISOString() : null,
    };
  }, []);

  const pushLiveState = useCallback(() => {
    if (finishedHandledRef.current || !matClaimedRef.current) return;
    const state = buildLiveState();
    if (!state) return;

    const connection = hubConnectionRef.current;
    const mat = liveSnapRef.current.matNumber;
    if (!mat || !connection || connection.state !== signalR.HubConnectionState.Connected) return;

    connection.invoke('PushMatchState', mat, state)
      .catch(err => console.error('[табло] не удалось отправить обновление счёта', err));
  }, [buildLiveState]);

  // Подключение к хабу табло. Открываем даже без выбранного ковра — чтобы видеть,
  // какие ковры уже заняты другими ноутбуками в этой сети.
  useEffect(() => {
    const connection = createMatchHubConnection();
    hubConnectionRef.current = connection;

    const applyLocks = (locks: MatLockDto[]) => {
      if (Array.isArray(locks)) setMatLocks(locks);
    };

    const applyClaim = (result: MatClaimResult | null | undefined) => {
      if (result?.accepted) {
        matClaimedRef.current = true;
        setMatClaimed(true);
        setMatClaimError(null);
        pushLiveState();
        return;
      }
      matClaimedRef.current = false;
      setMatClaimed(false);
      setMatClaimError(result?.message || 'Этот ковёр уже занят другим ноутбуком');
    };

    const claimIfNeeded = async () => {
      const mat = liveSnapRef.current.matNumber;
      try {
        const locks = await connection.invoke<MatLockDto[]>('GetMatLocks');
        applyLocks(locks);
      } catch {
        // список занятости — подсказка, не блокер
      }
      if (!mat) {
        matClaimedRef.current = false;
        setMatClaimed(false);
        setMatClaimError(null);
        return;
      }
      try {
        await connection.invoke('JoinMat', mat);
        const result = await connection.invoke<MatClaimResult>('ClaimMat', mat, controllerIdRef.current);
        applyClaim(result);
      } catch {
        matClaimedRef.current = false;
        setMatClaimed(false);
        setMatClaimError('Не удалось занять ковёр для табло');
      }
    };

    const resendCurrentState = () => {
      pushLiveState();
    };

    connection.on('StateRequested', resendCurrentState);
    connection.on('MatLocksUpdated', applyLocks);

    connection.start()
      .then(claimIfNeeded)
      .catch(err => console.error('[табло] не удалось подключиться к хабу', err));

    connection.onreconnected(() => {
      claimIfNeeded().catch(err => console.error('[табло] не удалось переподключиться к хабу', err));
    });

    return () => {
      const mat = liveSnapRef.current.matNumber;
      connection.off('StateRequested', resendCurrentState);
      connection.off('MatLocksUpdated', applyLocks);
      matClaimedRef.current = false;
      if (mat && connection.state === signalR.HubConnectionState.Connected) {
        connection.invoke('ReleaseMat', mat).catch(() => undefined);
      }
      connection.stop();
      hubConnectionRef.current = null;
    };
  }, [matNumber, pushLiveState]);

  // Автоматическая трансляция счёта на табло при любом изменении, которое видно на этой панели.
  // Специально не завязано на «тик» таймера каждую секунду — табло само крутит отсчёт
  // локально от момента получения снимка, чтобы не зависеть от часов монитора.
  // timeRemaining читается из liveSnapRef в pushLiveState, поэтому в deps его нет:
  // иначе каждую секунду уходил бы новый снимок и отсчёт на табло сбрасывался.
  useEffect(() => {
    if (!matNumber || !match) return;
    pushLiveState();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remaining ticks must not resend
  }, [
    matNumber,
    match,
    matchId,
    homeIppon,
    homeWazaAri,
    homeYuko,
    homeShido,
    awayIppon,
    awayWazaAri,
    awayYuko,
    awayShido,
    localStarted,
    localPaused,
    timerRunning,
    osaekomiActive,
    osaekomiStartTime,
    pushLiveState,
  ]);

  const handlePickMat = (value: string) => {
    setMatNumber(value);
    setMatNumberState(value);
  };

  useEffect(() => {
    if (!timerRunning) return;
    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1000;
        if (newTime <= 0) {
          setTimerRunning(false);
          handleTimeExpired();
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
    // handleTimeExpired читает liveSnapRef — актуальные очки, а не замыкание со старта.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerRunning]);

  useEffect(() => {
    if (osaekomiActive && osaekomiStartTime) {
      const interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - osaekomiStartTime) / 1000);
        setOsaekomiCurrentTime(elapsed);

        if (elapsed >= 20) {
          handleOsaekomiStop();
        }
      }, 100);

      return () => clearInterval(interval);
    } else {
      setOsaekomiCurrentTime(0);
    }
  }, [osaekomiActive, osaekomiStartTime]);

  const formatTime = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const canManageMatch = () => isAdmin() || hasRole('Судья');

  const handleStartLocal = () => {
    if (!canManageMatch() || !match) return;
    setLocalStarted(true);
    setLocalPaused(false);
    setLocalStartAt(Date.now());
    setTimerRunning(true);
    setActionSuccess('Поединок начат');
    setTimeout(() => setActionSuccess(null), 2000);
  };

  const handlePauseToggleLocal = () => {
    if (!canManageMatch() || !localStarted) return;
    if (!localPaused) {
      setLocalPaused(true);
      setLocalLastPauseAt(Date.now());
      setTimerRunning(false);
    } else {
      setLocalPaused(false);
      if (localLastPauseAt) {
        setLocalPausedAccumMs(prev => prev + (Date.now() - localLastPauseAt));
      }
      setLocalLastPauseAt(null);
      setTimerRunning(true);
    }
  };

  const submitViaBuiltInEndpoints = async (isHomeWinner: boolean) => {
    if (!match) return;

    try {
      const fresh = await matchClient.getMatch(match.id!);
      if (!fresh.startTime) {
        await matchClient.startMatch(match.id!);
      }
    } catch { }

    const addMany = async (type: ScoreType, count: number, isHome: boolean) => {
      for (let i = 0; i < count; i++) {
        await matchClient.addScore(match.id!, new ScoreModel({ scoreType: type, isHome }));
      }
    };

    await addMany(ScoreType.Yuko, homeYuko, true);
    await addMany(ScoreType.Yuko, awayYuko, false);

    await addMany(ScoreType.WazaAri, homeWazaAri, true);
    await addMany(ScoreType.WazaAri, awayWazaAri, false);

    if (homeIppon > 0 || awayIppon > 0) {
      if (homeIppon > 0) {
        await addMany(ScoreType.Ippon, 1, true);
      } else {
        await addMany(ScoreType.Ippon, 1, false);
      }
    }

    const afterScores = await matchClient.getMatch(match.id!);
    if (!afterScores.endTime) {
      await matchClient.endMatch(match.id!, new EndMatchModel({ isHomeWinner }));
    }
  };

  const finalizeAndSubmit = async (isHomeWinner: boolean, scoresOverride?: MatchScoresSnapshot) => {
    if (!match) return;
    const scores = scoresOverride ?? getScoresSnapshot();
    try {
      setTimerRunning(false);
      const payload = {
        isHomeWinner,
        homeScoreIppon: scores.homeIppon,
        homeScoreWazaAri: scores.homeWazaAri,
        homeScoreYuko: scores.homeYuko,
        awayScoreIppon: scores.awayIppon,
        awayScoreWazaAri: scores.awayWazaAri,
        awayScoreYuko: scores.awayYuko,
        homeShido: scores.homeShido,
        awayShido: scores.awayShido,
        startTime: localStartAt ? new Date(localStartAt).toISOString() : null,
        pausedMilliseconds: localPausedAccumMs
      } as any;

      await axios.post(`${ApiUrl}/api/Match/SubmitFinalResult?id=${match.id}`, payload);

      setActionSuccess('Результат поединка сохранен');
      setTimeout(() => setActionSuccess(null), 3000);

      const updated = await matchClient.getMatch(match.id!);
      setMatch(updated);
      notifyFinished(updated, isHomeWinner);
    } catch (e) {
      try {
        await submitViaBuiltInEndpoints(isHomeWinner);
        setActionSuccess('Результат поединка сохранен');
        setTimeout(() => setActionSuccess(null), 3000);
        const updated = await matchClient.getMatch(match.id!);
        setMatch(updated);
        notifyFinished(updated, isHomeWinner);
      } catch (ex) {
        handleError(appContext!.setNotificationData, ex);
      }
    }
  };

  const notifyFinished = (updated: Match, isHomeWinner: boolean) => {
    if (finishedHandledRef.current) return;
    finishedHandledRef.current = true;
    const winnerName = isHomeWinner
      ? `${updated.homeParticipant?.person?.lastName || ''} ${updated.homeParticipant?.person?.firstName || ''}`.trim()
      : `${updated.awayParticipant?.person?.lastName || ''} ${updated.awayParticipant?.person?.firstName || ''}`.trim();

    const connection = hubConnectionRef.current;
    if (connection && matNumber && connection.state === signalR.HubConnectionState.Connected) {
      connection.invoke('PushMatchEnded', matNumber, {
        matchId: updated.id || matchId,
        winnerName: winnerName || 'Победитель',
      }).catch(err => console.error('[табло] не удалось отправить итог поединка', err));
    }

    onFinished?.(winnerName || 'Победитель');
  };

  const enterManualMode = () => {
    if (!canManageMatch()) return;
    setManualMode(true);
    setLocalStarted(false);
    setTimerRunning(false);
    setLocalPaused(false);
    setOsaekomiActive(null);
    setOsaekomiStartTime(null);
  };

  const handleResetResult = async () => {
    if (!canManageMatch() || !match?.id) return;
    try {
      await axios.post(`${ApiUrl}/api/Match/ResetMatchResult?id=${match.id}`);
      finishedHandledRef.current = false;
      setManualMode(false);
      setLocalStarted(false);
      setTimerRunning(false);
      setLocalPaused(false);
      setActionSuccess('Результат отменён');
      setTimeout(() => setActionSuccess(null), 2500);
      setReloadToken(t => t + 1);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, apiErrorMessage(ex));
    }
  };

  const handleSaveManual = async (isHomeWinner: boolean) => {
    if (!canManageMatch() || !match?.id || savingManual) return;
    const scores = getScoresSnapshot();
    const homeWin = scores.homeIppon >= 1 || scores.homeWazaAri >= 2;
    const awayWin = scores.awayIppon >= 1 || scores.awayWazaAri >= 2;
    if (homeWin && awayWin) {
      setActionSuccess('Иппон или 2 вазари могут быть только у одного участника');
      setTimeout(() => setActionSuccess(null), 2500);
      return;
    }
    const durationSeconds = Math.max(0, manualMinutes * 60 + manualSeconds);
    setSavingManual(true);
    try {
      const wasFinished = Boolean(match.endTime && match.isHomeWinner !== undefined);
      if (wasFinished) {
        await axios.post(`${ApiUrl}/api/Match/ResetMatchResult?id=${match.id}`);
        finishedHandledRef.current = false;
      }
      const payload = {
        isHomeWinner,
        homeScoreIppon: scores.homeIppon,
        homeScoreWazaAri: scores.homeWazaAri,
        homeScoreYuko: scores.homeYuko,
        awayScoreIppon: scores.awayIppon,
        awayScoreWazaAri: scores.awayWazaAri,
        awayScoreYuko: scores.awayYuko,
        homeShido: scores.homeShido,
        awayShido: scores.awayShido,
        durationSeconds,
        pausedMilliseconds: 0,
      };
      await axios.post(`${ApiUrl}/api/Match/SubmitFinalResult?id=${match.id}`, payload);
      const updated = await matchClient.getMatch(match.id);
      setMatch(updated);
      setManualMode(false);
      setActionSuccess('Результат поединка сохранен');
      setTimeout(() => setActionSuccess(null), 3000);
      notifyFinished(updated, isHomeWinner);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex, apiErrorMessage(ex));
    } finally {
      setSavingManual(false);
    }
  };

  const handleScoreLocal = (type: 'ippon' | 'waza' | 'yuko', isHome: boolean) => {
    if (!canManageMatch()) return;
    if (!manualMode && (!localStarted || localPaused)) return;

    if (type === 'ippon') {
      const scores = getScoresSnapshot();
      if (isHome) {
        scores.homeIppon = Math.min(1, scores.homeIppon + 1);
        setHomeIppon(scores.homeIppon);
      } else {
        scores.awayIppon = Math.min(1, scores.awayIppon + 1);
        setAwayIppon(scores.awayIppon);
      }
      if (!manualMode) finalizeAndSubmit(isHome, scores);
      return;
    }

    if (type === 'waza') {
      const scores = getScoresSnapshot();
      if (isHome) {
        scores.homeWazaAri = Math.min(2, scores.homeWazaAri + 1);
        setHomeWazaAri(scores.homeWazaAri);
        if (!manualMode && scores.homeWazaAri >= 2) finalizeAndSubmit(true, scores);
      } else {
        scores.awayWazaAri = Math.min(2, scores.awayWazaAri + 1);
        setAwayWazaAri(scores.awayWazaAri);
        if (!manualMode && scores.awayWazaAri >= 2) finalizeAndSubmit(false, scores);
      }
      return;
    }

    if (type === 'yuko') {
      if (isHome) setHomeYuko(v => v + 1);
      else setAwayYuko(v => v + 1);
    }
  };

  const handleShido = (isHome: boolean) => {
    if (!canManageMatch()) return;
    if (!manualMode && (!localStarted || localPaused)) return;

    const scores = getScoresSnapshot();
    if (isHome) {
      scores.homeShido = Math.min(3, scores.homeShido + 1);
      setHomeShido(scores.homeShido);
      if (!manualMode && scores.homeShido >= 3) finalizeAndSubmit(false, scores);
    } else {
      scores.awayShido = Math.min(3, scores.awayShido + 1);
      setAwayShido(scores.awayShido);
      if (!manualMode && scores.awayShido >= 3) finalizeAndSubmit(true, scores);
    }
  };

  const bumpManual = (
    setter: React.Dispatch<React.SetStateAction<number>>,
    delta: number,
    min: number,
    max: number
  ) => {
    if (!manualMode || !canManageMatch()) return;
    setter(v => Math.min(max, Math.max(min, v + delta)));
  };

  const hasDecisiveWin = (ippon: number, wazaAri: number) => ippon >= 1 || wazaAri >= 2;
  const homeHasWin = hasDecisiveWin(homeIppon, homeWazaAri);
  const awayHasWin = hasDecisiveWin(awayIppon, awayWazaAri);

  const warnSingleWinner = () => {
    setActionSuccess('Иппон или 2 вазари могут быть только у одного участника');
    setTimeout(() => setActionSuccess(null), 2500);
  };

  const bumpManualIppon = (isHome: boolean, delta: number) => {
    if (delta > 0 && (isHome ? awayHasWin : homeHasWin)) {
      warnSingleWinner();
      return;
    }
    bumpManual(isHome ? setHomeIppon : setAwayIppon, delta, 0, 1);
  };

  const bumpManualWaza = (isHome: boolean, delta: number) => {
    const current = isHome ? homeWazaAri : awayWazaAri;
    if (delta > 0 && current + delta >= 2 && (isHome ? awayHasWin : homeHasWin)) {
      warnSingleWinner();
      return;
    }
    bumpManual(isHome ? setHomeWazaAri : setAwayWazaAri, delta, 0, 2);
  };

  const handleOsaekomiStart = (isHome: boolean) => {
    if (!canManageMatch() || !localStarted || localPaused || osaekomiActive) return;
    setOsaekomiActive(isHome ? 'home' : 'away');
    setOsaekomiStartTime(Date.now());
  };

  const handleOsaekomiStop = () => {
    const s = liveSnapRef.current;
    if (!s.osaekomiActive || !s.osaekomiStartTime) return;

    const elapsedSeconds = Math.floor((Date.now() - s.osaekomiStartTime) / 1000);

    if (s.osaekomiActive === 'home') {
      checkOsaekomiScore(elapsedSeconds, true);
    } else {
      checkOsaekomiScore(elapsedSeconds, false);
    }

    setOsaekomiActive(null);
    setOsaekomiStartTime(null);
    setOsaekomiCurrentTime(0);
  };

  // Правила удержания старших возрастов: 5-9с=Юко, 10-19с=Вазари, 20+с=Иппон
  const checkOsaekomiScore = (seconds: number, isHome: boolean) => {
    const scores = getScoresSnapshot();

    if (seconds >= 20) {
      if (isHome) {
        scores.homeIppon += 1;
        setHomeIppon(scores.homeIppon);
      } else {
        scores.awayIppon += 1;
        setAwayIppon(scores.awayIppon);
      }
      finalizeAndSubmit(isHome, scores);
    } else if (seconds >= 10) {
      if (isHome) {
        scores.homeWazaAri += 1;
        setHomeWazaAri(scores.homeWazaAri);
        if (scores.homeWazaAri >= 2) finalizeAndSubmit(true, scores);
      } else {
        scores.awayWazaAri += 1;
        setAwayWazaAri(scores.awayWazaAri);
        if (scores.awayWazaAri >= 2) finalizeAndSubmit(false, scores);
      }
    } else if (seconds >= 5) {
      if (isHome) {
        scores.homeYuko += 1;
        setHomeYuko(scores.homeYuko);
      } else {
        scores.awayYuko += 1;
        setAwayYuko(scores.awayYuko);
      }
    }
  };

  const handleTimeExpired = () => {
    const s = liveSnapRef.current;
    if (s.osaekomiActive) {
      handleOsaekomiStop();
    }

    if (s.homeWazaAri !== s.awayWazaAri) {
      finalizeAndSubmit(s.homeWazaAri > s.awayWazaAri);
      return;
    }
    if (s.homeYuko !== s.awayYuko) {
      finalizeAndSubmit(s.homeYuko > s.awayYuko);
      return;
    }
    setActionSuccess('Время истекло. Выберите победителя.');
    setTimeout(() => setActionSuccess(null), 3000);
  };

  if (loading) {
    return <div className="match-conduct__loading">Загрузка данных поединка...</div>;
  }

  if (!match) {
    return <div className="match-conduct__loading">Поединок не найден</div>;
  }

  const homeName = match.homeParticipant?.person
    ? `${match.homeParticipant.person.lastName} ${match.homeParticipant.person.firstName}`
    : 'Первый участник';
  const awayName = match.awayParticipant?.person
    ? `${match.awayParticipant.person.lastName} ${match.awayParticipant.person.firstName}`
    : 'Второй участник';

  const isMatchFinished = Boolean(match.endTime && match.isHomeWinner !== undefined);
  const canEditScores = canManageMatch() && (manualMode || (localStarted && !localPaused && !isMatchFinished));
  const showLiveButtons = canEditScores && !manualMode;
  const suggestedWinner = resolveSuggestedWinner({
    homeIppon, homeWazaAri, homeYuko, awayIppon, awayWazaAri, awayYuko, homeShido, awayShido,
  });

  return (
    <div className="match-conduct">
      {canManageMatch() && (
        <div className="match-conduct__mat-picker">
          {matNumber ? (
            <span>
              {matClaimed
                ? `Ковёр №${matNumber} · табло только с этого ноутбука`
                : `Ковёр №${matNumber}`}
              {!localStarted && (
                <>
                  {' '}
                  <button type="button" className="match-conduct__mat-picker-change" onClick={() => setMatNumberState(null)}>
                    изменить
                  </button>
                </>
              )}
            </span>
          ) : (
            <span>
              Выберите ковёр для табло:{' '}
              {['1', '2', '3', '4'].map(n => {
                const lock = matLocks.find(l => l.matNumber === n);
                const taken = Boolean(lock && lock.controllerId !== controllerIdRef.current);
                return (
                  <button
                    key={n}
                    type="button"
                    disabled={taken}
                    title={taken
                      ? (lock?.isLive
                        ? 'На этом табло уже идёт поединок'
                        : 'Ковёр занят другим ноутбуком')
                      : `Транслировать на табло ковра ${n}`}
                    onClick={() => handlePickMat(n)}
                  >
                    {n}
                  </button>
                );
              })}
            </span>
          )}
          {matClaimError && (
            <div className="match-conduct__mat-picker-error">{matClaimError}</div>
          )}
        </div>
      )}

      {actionSuccess && <div className="match-conduct__success">{actionSuccess}</div>}

      {isMatchFinished && (
        <div className="match-conduct__finished-banner">
          <h3>Поединок завершён</h3>
          <p>
            <strong>Победитель:</strong> {match.isHomeWinner ? homeName : awayName}
          </p>
          {canManageMatch() && !manualMode && (
            <div className="match-conduct__finished-actions">
              <button type="button" className="manual-button" onClick={enterManualMode}>
                Исправить вручную
              </button>
              <button type="button" className="reset-result-button" onClick={handleResetResult}>
                Отменить результат
              </button>
            </div>
          )}
        </div>
      )}

      <div className={`match-conduct__timer ${osaekomiActive ? 'osaekomi-active' : ''}`}>
        <h3>
          {manualMode
            ? formatTime((manualMinutes * 60 + manualSeconds) * 1000)
            : formatTime(timeRemaining)}
        </h3>
        {manualMode && (
          <div className="match-conduct__duration">
            <label>
              Длительность
              <input
                type="number"
                min={0}
                max={60}
                value={manualMinutes}
                onChange={e => setManualMinutes(Math.max(0, Number(e.target.value) || 0))}
              />
              мин
            </label>
            <label>
              <input
                type="number"
                min={0}
                max={59}
                value={manualSeconds}
                onChange={e => setManualSeconds(Math.min(59, Math.max(0, Number(e.target.value) || 0)))}
              />
              сек
            </label>
          </div>
        )}
        {osaekomiActive && !manualMode && (
          <div className="osaekomi-indicator">
            УДЕРЖАНИЕ ({osaekomiActive === 'home' ? homeName : awayName}): {osaekomiCurrentTime}с
          </div>
        )}
      </div>

      <div className="match-participants">
        <div className={`participant home ${(localStarted && !localPaused && (homeWazaAri >= 2 || homeIppon > 0)) || (isMatchFinished && match.isHomeWinner) ? 'winner' : ''}`}>
          <p className="participant-name">{homeName}</p>
          <div className="score-display">
            <div className="score-item">
              <span>Иппон: {homeIppon}</span>
              {showLiveButtons && (
                <button onClick={() => handleScoreLocal('ippon', true)} className="score-button">Иппон</button>
              )}
              {manualMode && canManageMatch() && (
                <span className="score-stepper">
                  <button type="button" className="score-button" onClick={() => bumpManualIppon(true, -1)}>−</button>
                  <button
                    type="button"
                    className="score-button"
                    disabled={homeIppon >= 1 || awayHasWin}
                    title={awayHasWin ? 'У соперника уже иппон или 2 вазари' : undefined}
                    onClick={() => bumpManualIppon(true, 1)}
                  >
                    +
                  </button>
                </span>
              )}
            </div>
            <div className="score-item">
              <span>Вазари: {homeWazaAri}</span>
              {showLiveButtons && (
                <button onClick={() => handleScoreLocal('waza', true)} className="score-button">Вазари</button>
              )}
              {manualMode && canManageMatch() && (
                <span className="score-stepper">
                  <button type="button" className="score-button" onClick={() => bumpManualWaza(true, -1)}>−</button>
                  <button
                    type="button"
                    className="score-button"
                    disabled={homeWazaAri >= 2 || (homeWazaAri >= 1 && awayHasWin)}
                    title={awayHasWin ? 'У соперника уже иппон или 2 вазари' : undefined}
                    onClick={() => bumpManualWaza(true, 1)}
                  >
                    +
                  </button>
                </span>
              )}
            </div>
            <div className="score-item">
              <span>Юко: {homeYuko}</span>
              {showLiveButtons && (
                <button onClick={() => handleScoreLocal('yuko', true)} className="score-button">Юко</button>
              )}
              {manualMode && canManageMatch() && (
                <span className="score-stepper">
                  <button type="button" className="score-button" onClick={() => bumpManual(setHomeYuko, -1, 0, 99)}>−</button>
                  <button type="button" className="score-button" onClick={() => bumpManual(setHomeYuko, 1, 0, 99)}>+</button>
                </span>
              )}
            </div>
            <div className="score-item">
              <span style={{ color: '#f59e0b' }}>Шидо: {homeShido}</span>
              {showLiveButtons && (
                <button onClick={() => handleShido(true)} className="score-button shido-button">Шидо</button>
              )}
              {manualMode && canManageMatch() && (
                <span className="score-stepper">
                  <button type="button" className="score-button shido-button" onClick={() => bumpManual(setHomeShido, -1, 0, 3)}>−</button>
                  <button type="button" className="score-button shido-button" onClick={() => bumpManual(setHomeShido, 1, 0, 3)}>+</button>
                </span>
              )}
            </div>
            {!isMatchFinished && !manualMode && (
              <div className="score-item">
                <span>Удержание</span>
                {localStarted && !localPaused && !osaekomiActive && (
                  <button onClick={() => handleOsaekomiStart(true)} className="score-button osaekomi-button">Начать</button>
                )}
                {osaekomiActive === 'home' && (
                  <button onClick={handleOsaekomiStop} className="score-button osaekomi-stop-button">МАТЕ</button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="vs-display">против</div>

        <div className={`participant participantAway ${(localStarted && !localPaused && (awayWazaAri >= 2 || awayIppon > 0)) || (isMatchFinished && !match.isHomeWinner) ? 'winner' : ''}`}>
          <p className="participant-name" style={{ textAlign: 'right' }}>{awayName}</p>
          <div className="score-display">
            <div className="score-item">
              {showLiveButtons && (
                <button onClick={() => handleScoreLocal('ippon', false)} className="score-button">Иппон</button>
              )}
              {manualMode && canManageMatch() && (
                <span className="score-stepper">
                  <button type="button" className="score-button" onClick={() => bumpManualIppon(false, -1)}>−</button>
                  <button
                    type="button"
                    className="score-button"
                    disabled={awayIppon >= 1 || homeHasWin}
                    title={homeHasWin ? 'У соперника уже иппон или 2 вазари' : undefined}
                    onClick={() => bumpManualIppon(false, 1)}
                  >
                    +
                  </button>
                </span>
              )}
              <span>Иппон: {awayIppon}</span>
            </div>
            <div className="score-item">
              {showLiveButtons && (
                <button onClick={() => handleScoreLocal('waza', false)} className="score-button">Вазари</button>
              )}
              {manualMode && canManageMatch() && (
                <span className="score-stepper">
                  <button type="button" className="score-button" onClick={() => bumpManualWaza(false, -1)}>−</button>
                  <button
                    type="button"
                    className="score-button"
                    disabled={awayWazaAri >= 2 || (awayWazaAri >= 1 && homeHasWin)}
                    title={homeHasWin ? 'У соперника уже иппон или 2 вазари' : undefined}
                    onClick={() => bumpManualWaza(false, 1)}
                  >
                    +
                  </button>
                </span>
              )}
              <span>Вазари: {awayWazaAri}</span>
            </div>
            <div className="score-item">
              {showLiveButtons && (
                <button onClick={() => handleScoreLocal('yuko', false)} className="score-button">Юко</button>
              )}
              {manualMode && canManageMatch() && (
                <span className="score-stepper">
                  <button type="button" className="score-button" onClick={() => bumpManual(setAwayYuko, -1, 0, 99)}>−</button>
                  <button type="button" className="score-button" onClick={() => bumpManual(setAwayYuko, 1, 0, 99)}>+</button>
                </span>
              )}
              <span>Юко: {awayYuko}</span>
            </div>
            <div className="score-item">
              {showLiveButtons && (
                <button onClick={() => handleShido(false)} className="score-button shido-button">Шидо</button>
              )}
              {manualMode && canManageMatch() && (
                <span className="score-stepper">
                  <button type="button" className="score-button shido-button" onClick={() => bumpManual(setAwayShido, -1, 0, 3)}>−</button>
                  <button type="button" className="score-button shido-button" onClick={() => bumpManual(setAwayShido, 1, 0, 3)}>+</button>
                </span>
              )}
              <span style={{ color: '#f59e0b' }}>Шидо: {awayShido}</span>
            </div>
            {!isMatchFinished && !manualMode && (
              <div className="score-item">
                {localStarted && !localPaused && !osaekomiActive && (
                  <button onClick={() => handleOsaekomiStart(false)} className="score-button osaekomi-button">Начать</button>
                )}
                {osaekomiActive === 'away' && (
                  <button onClick={handleOsaekomiStop} className="score-button osaekomi-stop-button">МАТЕ</button>
                )}
                <span>Удержание</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {canManageMatch() && !manualMode && !isMatchFinished && (
        <div className="match-conduct__actions">
          {!localStarted && (
            <>
              <button onClick={handleStartLocal} className="start-button">Начать поединок</button>
              <button type="button" className="manual-button" onClick={enterManualMode}>Указать вручную</button>
            </>
          )}

          {localStarted && (
            <button onClick={handlePauseToggleLocal} className={localPaused ? 'resume-button' : 'pause-button'}>
              {localPaused ? 'Возобновить' : 'Пауза'}
            </button>
          )}
        </div>
      )}

      {canManageMatch() && manualMode && (
        <div className="match-conduct__actions match-conduct__actions--manual">
          <p className="match-conduct__manual-hint">
            Проставьте оценки и длительность, затем выберите победителя.
            {suggestedWinner === true ? ` По оценкам лидирует ${homeName}.` : ''}
            {suggestedWinner === false ? ` По оценкам лидирует ${awayName}.` : ''}
          </p>
          <button
            type="button"
            className="start-button"
            disabled={savingManual}
            onClick={() => handleSaveManual(true)}
          >
            Победил {homeName}
          </button>
          <button
            type="button"
            className="start-button start-button--away"
            disabled={savingManual}
            onClick={() => handleSaveManual(false)}
          >
            Победил {awayName}
          </button>
          <button
            type="button"
            className="manual-button"
            disabled={savingManual}
            onClick={() => setManualMode(false)}
          >
            Назад
          </button>
        </div>
      )}
    </div>
  );
};

export default MatchConduct;
